import { isEmpty, isNil } from 'lodash';
// Import Services
import { Helpers } from 'services';
// Import Constants
import { l } from 'constants/common';
// Import Types
import { KpiListModel, ParseToUIModel } from './types';
//Import Enums
import { MetricTypeEnum } from 'enums';

const { customMomentWithoutTimezoneConversion } = Helpers;

const parseToUIModel: ParseToUIModel = ({
    testResultsAPI,
    promotionPresets,
    t,
    partnerDateFormat,
    campaignNextRunDate,
    currencyId,
    rate,
}) => {
    if (isNil(testResultsAPI) || isNil(promotionPresets)) {
        return null;
    }

    return testResultsAPI.map((result, index) => {
        const { PromotionTestPresetId } = result;
        const selectedPreset = promotionPresets.find(
            (preset) => preset.PromotionTestPresetId === PromotionTestPresetId,
        );

        const kpiIdNameGroup =
            selectedPreset?.PromotionTestMetrics.reduce((acc, { PromotionTestMetricId, Name, Type }) => {
                acc[PromotionTestMetricId] = { name: Name, type: Type };
                return acc;
            }, {}) ?? {};

        const isResultEmpty = isEmpty(result.ABTestResults);
        const getCurrencyCode = (currencyId: string, rate: number) => (isNil(rate) ? 'EUR' : currencyId);

        let kpiList = [] as KpiListModel[];

        if (!isNil(rate) && !isNil(currencyId)) {
            kpiList = isResultEmpty
                ? result.PromotionTestMetrics.map((kpiId) => ({
                      titleKey: kpiIdNameGroup[kpiId]?.name,
                      pilotGroup: 0,
                      controlGroup: 0,
                      impactPercentage: null,
                      confidencePercentage: null,
                      type: kpiIdNameGroup[kpiId]?.type,
                      currencyCode: getCurrencyCode(currencyId, rate),
                  }))
                : result.ABTestResults.map((kpiResult) => {
                      const kpi = kpiIdNameGroup[kpiResult.MetricId];
                      const hasRate = kpi?.type === MetricTypeEnum.Money && !isNil(rate);

                      return {
                          titleKey: kpi?.name,
                          pilotGroup: hasRate ? rate * kpiResult.TargetResult : kpiResult.TargetResult,
                          controlGroup: hasRate ? rate * kpiResult.ControlResult : kpiResult.ControlResult,
                          impactPercentage: kpiResult.Impact,
                          confidencePercentage: kpiResult.Significance,
                          type: kpi?.type,
                          currencyCode: getCurrencyCode(currencyId, rate),
                      };
                  });
        }

        const kpiNames = result.PromotionTestMetrics.map((kpiId) => kpiIdNameGroup[kpiId]?.name);

        const campaignStartDateMoment = !isNil(campaignNextRunDate)
            ? customMomentWithoutTimezoneConversion(campaignNextRunDate)
            : null;

        const abTestStartDateMoment = result?.StartDate
            ? customMomentWithoutTimezoneConversion(result.StartDate)
            : campaignStartDateMoment;

        const abTestStartDate = abTestStartDateMoment ? abTestStartDateMoment.format(partnerDateFormat) : t(l.NA);

        const abTestEndDate = abTestStartDateMoment
            ? abTestStartDateMoment.clone().add(result.Duration, 'days').format(partnerDateFormat)
            : t(l.NA);

        const segmentSplitEndDate = abTestStartDateMoment
            ? abTestStartDateMoment
                  .clone()
                  .add(result.Duration - result.AnalyticsPeriod, 'days')
                  .format(partnerDateFormat)
            : t(l.NA);

        return {
            tabTitle: `${t(l.ABTest)} ${index + 1}`,
            generalDetails: {
                goal: selectedPreset?.Name || '',
                segmentSplitEndDate: segmentSplitEndDate,
                kpiNames: kpiNames,
                endDate: abTestEndDate,
                startDate: abTestStartDate,
                controlGroup: result.SampleSize,
                pilotGroup: 100 - result.SampleSize,
                status: result.Status,
                reason: result.Reason,
                targetCount: result.TargetCount,
                controlCount: result.ControlCount,
                analyticsPeriod: result.AnalyticsPeriod,
                isExclusive: result.IsExclusive,
                exclusionPeriod: result.ExclusionPeriod || null,
            },
            kpiList: kpiList,
            isResultEmpty,
        };
    });
};

export { parseToUIModel };
